import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  createPendingAnimatedUploadStepSlice,
  type PendingAnimatedUploadStepSlice,
} from '~/components/v2/store/features/onboarding/slices/createPendingAnimatedUploadStepSlice';
import {
  createShouldMountOnboardingIntroSlice,
  type ShouldMountOnboardingIntroSlice,
} from '~/components/v2/store/features/onboarding/slices/createShouldMountOnboardingIntroSlice';
import {
  createShowFirstUploadPromptSlice,
  type ShowFirstUploadPromptSlice,
} from '~/components/v2/store/features/onboarding/slices/createShowFirstUploadPromptSlice';
import {
  createWorkspaceAgentIntroSlice,
  type WorkspaceAgentIntroSlice,
} from '~/components/v2/store/features/onboarding/slices/createWorkspaceAgentIntroSlice';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';

export type OnboardingPreferencesState = ShowFirstUploadPromptSlice &
  ShouldMountOnboardingIntroSlice &
  WorkspaceAgentIntroSlice &
  PendingAnimatedUploadStepSlice;

const VERSION_SHOW_FIRST_UPLOAD_PROMPT_RECORD = 2;
const VERSION_SHOULD_MOUNT_ONBOARDING_INTRO = 3;

const migrateShowFirstUploadPromptRecord = (persistedState: Record<string, unknown>): Record<string, unknown> => {
  if (
    !('showFirstUploadPrompt' in persistedState) ||
    typeof persistedState.showFirstUploadPrompt !== 'object' ||
    persistedState.showFirstUploadPrompt === null
  ) {
    return { ...persistedState, showFirstUploadPrompt: {} };
  }

  const typed = Object.fromEntries(Object.entries(persistedState.showFirstUploadPrompt));

  return {
    ...persistedState,
    showFirstUploadPrompt: typeof typed.workspaceId === 'string' ? { [typed.workspaceId]: typed.value === true } : {},
  };
};

const migrateHideSkipIntroToShouldMount = (persistedState: Record<string, unknown>): Record<string, unknown> => {
  const hideSkipIntro = persistedState.hideSkipIntro;
  const { hideSkipIntro: _removedHideSkipIntro, ...withoutHideSkipIntro } = persistedState;

  if (typeof hideSkipIntro !== 'object' || hideSkipIntro === null) {
    return { ...withoutHideSkipIntro, shouldMountOnboardingIntro: {} };
  }

  return { ...withoutHideSkipIntro, shouldMountOnboardingIntro: hideSkipIntro };
};

export const migrateOnboardingPreferencesState = (persistedState: unknown, version: number): unknown => {
  if (typeof persistedState !== 'object' || persistedState === null) {
    return persistedState;
  }

  let next = Object.fromEntries(Object.entries(persistedState));

  if (version < VERSION_SHOW_FIRST_UPLOAD_PROMPT_RECORD) {
    next = migrateShowFirstUploadPromptRecord(next);
  }

  if (version < VERSION_SHOULD_MOUNT_ONBOARDING_INTRO) {
    next = migrateHideSkipIntroToShouldMount(next);
  }

  return next;
};

export const useOnboardingPreferencesStore = create<OnboardingPreferencesState>()(
  persist(
    (...args) => ({
      ...createShowFirstUploadPromptSlice(...args),
      ...createShouldMountOnboardingIntroSlice(...args),
      ...createWorkspaceAgentIntroSlice(...args),
      ...createPendingAnimatedUploadStepSlice(...args),
    }),
    {
      name: ZustandStorageKeys.OnboardingPreferences,
      partialize: (state) => ({
        showFirstUploadPrompt: state.showFirstUploadPrompt,
        shouldMountOnboardingIntro: state.shouldMountOnboardingIntro,
        workspaceAgentIntro: state.workspaceAgentIntro,
        pendingAnimatedQuestionsStep: state.pendingAnimatedQuestionsStep,
        pendingAnimatedUploadStep: state.pendingAnimatedUploadStep,
      }),
      storage: createJSONStorage(() => localStorage),
      version: VERSION_SHOULD_MOUNT_ONBOARDING_INTRO,
      migrate: migrateOnboardingPreferencesState,
    },
  ),
);

export const useShowFirstUploadPromptValue = (workspaceId?: string): boolean =>
  useOnboardingPreferencesStore((state) => (workspaceId ? (state.showFirstUploadPrompt[workspaceId] ?? false) : false));

/** True only after create-workspace onboarding opted this workspace into the intro overlay. */
export const useShouldMountOnboardingIntroValue = (workspaceId: string | undefined): boolean =>
  useOnboardingPreferencesStore((state) =>
    workspaceId ? state.shouldMountOnboardingIntro[workspaceId] === true : false,
  );

export const usePendingAnimatedUploadStepValue = () =>
  useOnboardingPreferencesStore((state) => state.pendingAnimatedUploadStep);

/** True until this account has dismissed the agent intro on this browser. */
export const useIsOnboardingIntroPending = (accountId: string | undefined): boolean =>
  useOnboardingPreferencesStore((state) => !!accountId && state.workspaceAgentIntro[accountId]?.watched !== true);

/** True after this account has finished the intro at least once on this browser. */
export const useHasSeenOnboardingIntro = (accountId: string | undefined): boolean =>
  useOnboardingPreferencesStore((state) => !!accountId && state.workspaceAgentIntro[accountId]?.hasSeenIntro === true);
